import { cloneDeep, get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import Constants, {
  ItemTypeConsts,
  PasswordConsts,
  StoreModelMap,
} from 'client/util/Constants';
import ModalEventBus from 'components/Modal/ModalEventBus';

const { error, success } = Constants;
const customPasswordComplianceConst = PasswordConsts.passwordCompliance.custom;
const settingsModel = StoreModelMap[ItemTypeConsts.settings];

export default {
  data: () => ({
    enableMainAction: false,
    fileUploadCount: 0,
    isMounted: false,
    loading: false,
    targetAttribute: '_blank',
  }),

  computed: {
    ...mapState(settingsModel, [
      'item',
      'previousItem',
    ]),
  },

  mounted() {
    this.handleMounted();
  },

  beforeDestroy() {
    this.handleBeforeDestroy();
  },

  ModalEventBus,

  methods: {
    ...mapActions(settingsModel, {
      fetchOrgSettings: 'fetchItem',
      resetItem: 'resetItem',
      saveItem: 'saveItem',
      updateItem: 'updateItem',
      updatePreviousItem: 'updatePreviousItem',
    }),

    ...mapGetters(settingsModel, ['hasItemChanged']),

    /**
     * @description Reset the models to their default state on exit
     */
    handleBeforeDestroy() {
      this.resetItem();
    },

    /**
     * @description If the user chooses to cancel then reset the store model and
     * close the modal.
     */
    handleCancel() {
      if (this.hasModelChanged()) {
        this.$options.ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            headerText: 'Undo Changes to Settings',
            mdTemplate: 'Any unsaved changes you have made will be lost. Would you like to continue?',
            handleContinue: this.handleContinue,
            showClose: true,
          },
          preventCloseOnOverlayClick: true,
        });
      }
    },

    /**
     * @description The user is canceling so reset the store model and close
     * the modal.
     */
    async handleContinue() {
      await this.resetModel();
      this.$options.ModalEventBus.close();
    },

    /**
     * @description Get the model for use locally
     */
    handleMounted() {
      this.loading = true;

      this.fetchOrgSettings()
        .then(() => {
          this.isMounted = true;
        })
        .catch(() => {
          /**
           * @todo If this call fails, the security tab will not show any controls.
           * Might need to refine this behavior in further re-factors.
           */
          notify(error, 'Unable to load settings.');
        })
        .finally(() => {
          this.enableMainAction = false;
          this.loading = false;
        });
    },

    async handleSave() {
      // Do not continue if there are validation errors
      await this.$validator.validateAll();
      if (this.v2Errors.any()) {
        const otherErrors = this.v2Errors.items.filter(err => err.scope == null);

        if (otherErrors.length > 0) {
          const firstError = otherErrors[0];
          let errorMessage;
          if (firstError.rule === 'required' || firstError.rule === 'numeric') {
            errorMessage = 'A valid number must be specified.';
          }
          notify(error, errorMessage || firstError.msg);
        }

        return;
      }

      this.updateSettings();
    },

    handleTimeoutDurationChange(value) {
      const payload = {
        idleSessionDurationMinutes: value,
      };
      this.updateItem({ userPortal: payload });
    },

    // Check to see if either the password settings have changed
    hasModelChanged() {
      return this.hasItemChanged();
    },

    async resetModel() {
      // Reset the item in case the user has changed something that
      // wasn't initially set - if it's not reset those fields will persist
      // even after the updateItem - the logo, for example
      const { previousItem } = this;
      this.resetItem();

      // Restore to the original state
      await this.updateItem(cloneDeep(previousItem));
      await this.updatePreviousItem(cloneDeep(previousItem));

      // Reset vee-validate errors on resetModel
      this.v2Errors.clear();

      // Increment count to force the UploadFile component to re-render and
      // reset the main action button
      this.fileUploadCount += 1;
      this.enableMainAction = false;
    },

    /**
     * @description `/api/organizations` and `/api/settings` currently contain duplicate
     * information so you need to save both.
     *
     * Note: passwordCompliance is set to `custom` for all _save_ actions (this is a legacy item)
     */
    saveOrgSettings() {
      if (!this.hasItemChanged()) return Promise.resolve();

      const newPasswordPolicy = { ...this.currentPasswordPolicy, ...this.item.passwordPolicy };

      /**
       * The user was previously allowed to specify a number of days for the grace period.
       * We are moving away from that model to forcing the user to change their password at
       * log in instead. The field `enableDaysAfterExpirationToSelfRecover` is being re-purposed
       * to indicate whether the user will be forced to change their password at log in.
       * If the admin changed the value of this field (by checking or unchecking) then we don't
       * want to send the `days AfterExpirationToSelfRecover`; we need to continue to send
       * it otherwise.
       * @see https://jumpcloud.atlassian.net/browse/DE-1310
       */
      if (newPasswordPolicy.enableDaysAfterExpirationToSelfRecover
        !== this.previousItem.passwordPolicy.enableDaysAfterExpirationToSelfRecover) {
        delete newPasswordPolicy.daysAfterExpirationToSelfRecover;
      }

      const payload = {
        ...this.item,
        passwordCompliance: customPasswordComplianceConst,
        passwordPolicy: newPasswordPolicy,
      };

      this.updateItem(payload);

      return this.saveItem()
        .then(() => {
          // Increment count to force UploadFile component to hide `remove file` link
          this.fileUploadCount += 1;
        })
        .then(() => {
          notify(success, 'Settings have been updated.');
        })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');
          notify(error, message);
          throw new Error(message);
        });
    },

    updateSettings() {
      this.loading = true;

      return this.saveOrgSettings()
        .then(() => {
          this.loading = false;
          this.enableMainAction = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },

  watch: {
    item: {
      handler() {
        this.enableMainAction = this.hasItemChanged();
      },
      deep: true,
    },
  },
};
